import styled from '@emotion/styled'
import { colors, fonts } from 'component-library/__styling/settings'
import { Button } from 'component-library/buttons/Button/Button'
import { calcRem, breakpoint } from 'component-library/__styling/style-functions'
import { textCrop, visuallyhidden } from 'component-library/__styling/style-mixins'
import { themedValue } from 'component-library/__styling/themed-value'
import { TextLink } from 'component-library/typography/TextLink/TextLink'
import { ThemeMargins } from 'component-library/__styling/settings/metrics'

const westBgImage: string = require('./images/TW_Newsletter-image2.jpg')
const pnBgImage: string = require('./images/PN_Newsletter-662-V2.jpg')

// Colours
const pnBlue = colors.perthnow.blueSapphire

interface StyledContainerProps {
    verticalSpacing?: keyof ThemeMargins
}

export const StyledContainer = styled('div')<StyledContainerProps>(
    {
        position: 'relative',
        backgroundPosition: 'center left',
        backgroundRepeat: 'no-repeat',
        padding: calcRem(12),

        [breakpoint('xxs')]: {
            height: 126,
            padding: 0,
            backgroundColor: 'transparent',
        },

        [breakpoint('xs')]: {
            height: 108,
        },
    },
    ({ theme, verticalSpacing }) => ({
        marginBottom: verticalSpacing && calcRem(theme.margins[verticalSpacing]),
        backgroundColor: themedValue(theme, {
            thewest: section => section.primaryColor,
            perthnow: pnBlue,
            sevennews: undefined,
        }),

        [breakpoint('xxs')]: {
            backgroundImage: themedValue(theme, {
                thewest: `url(${westBgImage})`,
                perthnow: `url(${pnBgImage})`,
                sevennews: undefined,
            }),
        },
    })
)

export const StyledIcon = styled('div')({
    display: 'none',

    '& > svg': {
        fill: colors.white,
        width: 21,
        height: 21,
    },

    [breakpoint('xxs')]: {
        display: 'block',
        position: 'absolute',
        top: calcRem(12),
        left: calcRem(12),
    },
})

export const StyledFormContainer = styled('div')({
    [breakpoint('xxs')]: {
        padding: calcRem(8, 12, 12),
        width: '80%',
        marginLeft: 'auto',
        display: 'flex',
        flexWrap: 'wrap',
        textAlign: 'right',
    },
})

const westAngleDistance = 11
const westAngleDegrees = '134deg'
const pnAngleDegrees = '124deg'

export const StyledContentContainer = styled('div')(
    {
        height: '100%',
        marginLeft: 'auto',
        position: 'relative',
        display: 'flex',
    },
    ({ theme }) => ({
        backgroundImage: themedValue(theme, {
            thewest: section =>
                `linear-gradient(${westAngleDegrees}, rgba(2,0,36,0) 0%, rgba(4,61,226,0) ${westAngleDistance}%, ${section.primaryColor} ${westAngleDistance}%)`,
            perthnow: `linear-gradient(${pnAngleDegrees}, rgba(2,0,36,0) 0%, rgba(4,61,226,0) 22%, ${pnBlue} 22%)`,
            sevennews: undefined,
        }),

        [breakpoint('xxs')]: {
            backgroundImage: themedValue(theme, {
                thewest: section =>
                    `linear-gradient(${westAngleDegrees}, rgba(2,0,36,0) 0%, rgba(4,61,226,0) 28%, ${section.primaryColor} 28%)`,
                perthnow: `linear-gradient(${pnAngleDegrees}, rgba(2,0,36,0) 0%, rgba(4,61,226,0) 28%, ${pnBlue} 28%)`,
                sevennews: undefined,
            }),
            width: themedValue(theme, {
                thewest: 318,
                perthnow: 340,
                sevennews: undefined,
            }),
        },

        [breakpoint('xs')]: {
            width: themedValue(theme, {
                thewest: '70%',
                perthnow: '70%',
                sevennews: undefined,
            }),
        },

        [breakpoint('md')]: {
            backgroundImage: themedValue(theme, {
                thewest: section =>
                    `linear-gradient(${westAngleDegrees}, rgba(2,0,36,0) 0%, rgba(4,61,226,0) 22%, ${section.primaryColor} 22%)`,
                perthnow: `linear-gradient(${pnAngleDegrees}, rgba(2,0,36,0) 0%, rgba(4,61,226,0) 22%, ${pnBlue} 22%)`,
                sevennews: undefined,
            }),
        },
    })
)

export const StyledText = styled('p')(
    {
        margin: calcRem(0, 0, 10),
        color: colors.white,
        fontWeight: 700,
        lineHeight: calcRem(21),

        [breakpoint('xxs')]: {
            marginLeft: 'auto',
            maxWidth: 200,
        },

        [breakpoint('xs')]: {
            lineHeight: calcRem(24),
            marginBottom: calcRem(6),
            maxWidth: 280,
        },
    },
    ({ theme }) => ({
        fontFamily: themedValue(theme, {
            thewest: theme.fonts.heading,
            perthnow: fonts.perthnow.sansSerif,
            sevennews: undefined,
        }),
        fontSize: themedValue(theme, {
            thewest: calcRem(18),
            perthnow: calcRem(18),
            sevennews: undefined,
        }),

        [breakpoint('xs')]: {
            fontSize: themedValue(theme, {
                thewest: calcRem(18),
                perthnow: calcRem(20),
                sevennews: undefined,
            }),
        },
    })
)

export const StyledTextLink = styled(TextLink)({
    color: colors.white,
})

export const StyledForm = styled('form')({
    display: 'flex',

    [breakpoint('xs')]: {
        marginLeft: 'auto',
    },
})

export const StyledLabel = styled('label')(visuallyhidden)

export const StyledInput = styled('input')({
    marginRight: calcRem(10),
    padding: calcRem(0, 6),
    height: calcRem(33),
    border: `1px solid ${colors.white}`,
    borderRadius: '0',
    fontSize: calcRem(14),
    width: '100%',

    [breakpoint('xxs')]: {
        width: 150,
    },

    [breakpoint('xs')]: {
        width: 190,
        marginLeft: 'auto',
    },
})

export const StyledButton = styled(Button)(
    {
        margin: 0,
        padding: calcRem(9),
        height: calcRem(33),
        border: `1px solid ${colors.white}`,
        color: colors.white,
        ...textCrop({
            fontSize: 14,
            desiredLineHeight: 1,
            cropLineHeight: 1.1,
            topAdjustment: 9,
            bottomAdjustment: 8,
        }),
        maxWidth: 80,

        '&:hover': {
            backgroundColor: colors.white,
        },

        '&:focus': {
            backgroundColor: colors.white,
            color: colors.darkGrey,
            textDecoration: 'underline',
        },
    },
    ({ theme }) => ({
        '&:hover': {
            color: themedValue(theme, {
                thewest: section => section.primaryColor,
                perthnow: pnBlue,
                sevennews: undefined,
            }),
            border: themedValue(theme, {
                thewest: undefined,
                perthnow: `1px solid ${colors.white}`,
                sevennews: undefined,
            }),
        },

        '&:focus': {
            border: themedValue(theme, {
                thewest: undefined,
                perthnow: `1px solid ${colors.white}`,
                sevennews: undefined,
            }),
        },
    })
)
